import { useCallback, useEffect, useMemo, useState } from "react";
import { getCookieToken } from "../../../utils/get-auth-user";
import notificationsCenter from "../../../singleton/NotificationsCenter";
import { LogMessageType } from "../../../utils/messages";
import { DEFAULT_ASSETS } from "../utils/defaultAssets";
import { setPublicArtApi, fetchNgaCollections, queryArt, fetchMetCollections, PER_PAGE } from "../utils/publicArtApi";
import { setApiKey, fetchAssets as fetchLibrariesAssets, fetchCategories } from "../utils/librariesApi";

const fileTypeOptions = [
  { key: "all", text: "All" },
  { key: "model", text: "3D Model" },
  { key: "image", text: "Image" },
  { key: "video", text: "Video" },
  { key: "audio", text: "Audio" },
  { key: "gif", text: "GIF" },
  { key: "pdf", text: "PDF" }
];

const sourceOptions = [
  { key: "nga", text: "National Gallery of Art" },
  { key: "met", text: "The Metropolitan Museum of Art" }
];

const sourceOptionsLibraries = [
  {key: "sketchfab", text: "Sketchfab" },
]

const tabs = [
  { value: "files", label: "Files" },
  { value: "public-domain-art", label: "Public Domain Art" },
  { value: "libraries", label: "Libraries" }
];

const extractCursor = url => {
  if (!url) return null;

  const urlObj = new URL(url);
  return urlObj.searchParams.get("cursor") || urlObj.searchParams.get("page");
};

export function useFetchAssets({
  assetsEndpoint,
  publicDomainArtEndpoint,
  librariesApiKey,
  defaultToken = null,
  signedIn
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [selectedTab, setSelectedTab] = useState(signedIn ? "files" : "public-domain-art");
  // Set this to false to skip the first fetch under development
  const [loaded, setLoaded] = useState(true);
  const [collections, setCollections] = useState({});
  const [source, setSource] = useState("nga");
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [placeLoading, setPlaceLoading] = useState(false);

  const onChangePlaceLoading = useCallback(
    value => {
      setPlaceLoading(value);
    },
    [setPlaceLoading]
  );

  const hasNext = useMemo(() => !!nextCursor || !!nextPage, [nextCursor, nextPage]);
  const hasPrev = useMemo(() => prevCursor !== null || !!prevPage, [prevCursor, prevPage]);
  const editable = useMemo(() => selectedTab === tabs[0].value, [selectedTab]);

  const onNext = useCallback(() => {
    setPage(nextPage);
    setCursor(nextCursor);
  }, [nextCursor, nextPage]);

  const onPrev = useCallback(() => {
    setPage(prevPage);
    setCursor(prevCursor);
  }, [prevCursor, prevPage]);

  // Create a computed variable that will define to show fileTypeOptions, collections.nga or collections.met
  const options = useMemo(() => {
    switch (selectedTab) {
      case "files":
        return fileTypeOptions;
      case "public-domain-art":
        return source === "met" ? collections.met : collections.nga;
      case "libraries":
        return collections.libraries;
      default:
        return [];
    }
  }, [selectedTab, collections.nga, collections.met, collections.libraries, source]);

  const formatCollections = useCallback(collections => {
    // Add the "All" option to the beginning of the collections array
    const allOption = { key: "all", text: "All" };

    const formattedCollections = collections.map(collection => {
      return {
        key: collection.id || collection.slug || collection.name,
        text: collection.name
      };
    });

    return [allOption, ...formattedCollections];
  }, []);

  useEffect(() => {
    setPublicArtApi(publicDomainArtEndpoint);
    setApiKey(librariesApiKey);
  }, [publicDomainArtEndpoint, librariesApiKey]);

  useEffect(() => {
    const loadCollections = async () => {
      const ngaCollections = await fetchNgaCollections();
      const metCollections = await fetchMetCollections();
      const librariesCollections = await fetchCategories();

      setCollections(collections => ({
        ...collections,
        nga: formatCollections(ngaCollections),
        met: formatCollections(metCollections),
        libraries: formatCollections(librariesCollections)
      }));
    };

    loadCollections();
  }, [formatCollections]);

  // Create a function to rename, it will call the API with the asset id and the new name
  const renameAsset = useCallback(
    async ({ id, title }) => {
      const token = getCookieToken() || defaultToken;

      if (!token) {
        setError("No token");
        return;
      }

      const assetsUrl = assetsEndpoint + "/" + id;

      try {
        const response = await fetch(assetsUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ title })
        });

        if (!response.ok) {
          throw new Error("Failed to rename asset");
        }
        await response.json();
        setAssets(assets.map(asset => (asset.id === id ? { ...asset, title } : asset)));

        notificationsCenter.success(LogMessageType.assetRenamed);
      } catch (err) {
        setError(err.message);
        notificationsCenter.error(LogMessageType.assetRenameFailed);
      }
    },
    [assets, defaultToken, assetsEndpoint]
  );

  const deleteAsset = useCallback(
    async ({ id }) => {
      const token = getCookieToken() || defaultToken;

      if (!token) {
        setError("No token");
        return;
      }

      const assetsUrl = assetsEndpoint + "/" + id;

      try {
        const response = await fetch(assetsUrl, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error("Failed to delete asset");
        }

        setAssets(assets.filter(asset => asset.id !== id));
        notificationsCenter.success(LogMessageType.assetDeleted);
      } catch (err) {
        setError(err.message);
        notificationsCenter.error(LogMessageType.assetDeleteFailed);
      }
    },
    [assets, defaultToken, assetsEndpoint]
  );

  const handleFileTypeFilterChange = useCallback(
    (_, data) => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setPrevCursor(null);
      setSearchQuery("");
      setFileTypeFilter(data.optionValue);
    },
    [setFileTypeFilter]
  );

  const handleSourceChange = useCallback(
    (_, data) => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setPrevCursor(null);
      setSearchQuery("");
      setFileTypeFilter("all");
      setSource(data.optionValue);
    },
    [setSource]
  );

  const handleSelectedTabChange = useCallback(
    value => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setSearchQuery("");
      setFileTypeFilter("all");
      setSource("nga");
      setSelectedTab(value);
    },
    [setSelectedTab]
  );

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchAssets() {
      const token = getCookieToken() || defaultToken;

      if (!token) {
        setError("No token");
        return;
      }

      setLoading(true);
      try {
        let assetsUrl = assetsEndpoint;
        assetsUrl = `${assetsUrl}?asset_type=my-media`;

        if (debouncedQuery) {
          assetsUrl = `${assetsUrl}&q=${debouncedQuery}`;
        }

        if (fileTypeFilter !== "all") {
          assetsUrl = `${assetsUrl}&type=${fileTypeFilter}`;
        }

        assetsUrl = `${assetsUrl}&per_page=${PER_PAGE}`;

        if (page) {
          assetsUrl = `${assetsUrl}&page=${page}`;
        }

        const response = await fetch(assetsUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          signal
        });

        if (!response.ok) {
          throw new Error("Failed to fetch assets");
        }

        const data = await response.json();
        setAssets(data.data);
        setNextPage(extractCursor(data.links.next));
        setPrevPage(extractCursor(data.links.prev));
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          console.err(err);
        }
      } finally {
        setLoading(false);
      }
    }

    async function fetchPublicArt() {
      try {
        setLoading(true);
        const data = await queryArt(
          source,
          fileTypeFilter === "all" ? null : fileTypeFilter,
          page,
          debouncedQuery,
          signal
        );
        setNextPage(data[1].meta.next_page);
        setPrevPage(data[1].meta.prev_page);
        setAssets(data[1].entries);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setLoading(false);
          setError(err.message);
        }
      }
    }

    async function fetchLibraries() {
      try {
        setLoading(true);
        const data = await fetchLibrariesAssets(
          fileTypeFilter === "all" ? null : fileTypeFilter,
          cursor,
          debouncedQuery,
          signal
        );
        setNextCursor(data[1].meta.next_cursor);
        setPrevCursor(data[1].meta.prev_cursor);
        setAssets(data[1].entries);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          setLoading(false);
        }
      } finally {
        // setLoading(false);
      }
    }

    if (selectedTab === "files") {
      if (loaded) {
        fetchAssets();
      } else {
        // Use this under development only to skip the first fetch
        setAssets(DEFAULT_ASSETS);
        setLoading(false);
        setLoaded(true);
      }
    } else if (selectedTab === "public-domain-art") {
      fetchPublicArt();
    } else if (selectedTab === "libraries") {
      fetchLibraries();
    }

    return () => {
      controller.abort();
    };
  }, [debouncedQuery, fileTypeFilter, assetsEndpoint, defaultToken, selectedTab, source, cursor, page]);

  const filteredTabs = useMemo(() => {
    if (!signedIn) {
      return tabs.filter(tab => tab.value !== "files");
    }
    return tabs;
  }, [signedIn]);

  useEffect(() => {
    if(selectedTab == "libraries") {
      setSource("sketchfab");
    }
  }, [selectedTab])

  return {
    assets,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fileTypeFilter,
    handleFileTypeFilterChange,
    fileTypeOptions: options,
    renameAsset,
    deleteAsset,
    tabs: filteredTabs,
    selectedTab,
    handleSelectedTabChange,
    sourceOptions: selectedTab === "public-domain-art" 
      ? sourceOptions 
      : selectedTab === "libraries"
        ? sourceOptionsLibraries
        : []
    ,
    handleSourceChange,
    source,
    hasNext,
    hasPrev,
    onNext,
    onPrev,
    editable,
    onChangePlaceLoading,
    placeLoading
  };
}
